<template>
  <div class="identity-page">
    <div v-if="loading" class="identity-loading">加载中...</div>

    <template v-else-if="loadError">
      <div class="identity-state error">{{ loadError }}</div>
    </template>

    <template v-else>
      <section class="identity-card">
        <h1 class="identity-title">实人认证</h1>
        <p class="identity-desc">请填写姓名、身份证号和手机号，核验通过后可使用微信/支付宝完成支付。</p>
        <div class="identity-amount">{{ amountText }}</div>
        <p class="identity-subject">{{ pageInfo.subject || '' }}</p>
        <p v-if="pageInfo.mchName" class="identity-mch">付款给 {{ pageInfo.mchName }}</p>
      </section>

      <section v-if="!identityVerified" class="identity-card">
        <label class="identity-label">姓名</label>
        <input
          v-model.trim="form.userName"
          class="identity-input"
          maxlength="32"
          placeholder="请输入真实姓名"
        >

        <label class="identity-label">身份证号</label>
        <input
          v-model.trim="form.identifyNum"
          class="identity-input"
          maxlength="18"
          placeholder="请输入身份证号"
        >

        <label class="identity-label">手机号</label>
        <input
          v-model.trim="form.mobile"
          class="identity-input"
          maxlength="11"
          inputmode="numeric"
          placeholder="请输入手机号"
        >

        <button
          class="identity-btn identity-btn-primary"
          :disabled="verifying"
          @click="handleVerify"
        >
          {{ verifying ? '核验中...' : '提交核验' }}
        </button>
        <p v-if="statusMsg" :class="['identity-status', statusType]">{{ statusMsg }}</p>
      </section>

      <section v-else class="identity-card">
        <h2 class="identity-title identity-title-sm">选择支付方式</h2>
        <div class="identity-verified">
          <p>核验已通过</p>
          <p>姓名：{{ pageInfo.nameMask || '--' }}</p>
          <p>身份证：{{ pageInfo.idMask || '--' }}</p>
          <p>手机号：{{ pageInfo.phoneMask || '--' }}</p>
        </div>

        <button
          v-if="presetWayCode === 'WX_H5'"
          class="identity-btn identity-btn-wx"
          :disabled="paying"
          @click="handlePay()"
        >微信支付</button>
        <button
          v-else-if="presetWayCode === 'ALI_WAP'"
          class="identity-btn identity-btn-ali"
          :disabled="paying"
          @click="handlePay()"
        >支付宝支付</button>
        <template v-else>
          <button class="identity-btn identity-btn-wx" :disabled="paying" @click="handlePay('WX_H5')">微信支付</button>
          <button class="identity-btn identity-btn-ali" :disabled="paying" @click="handlePay('ALI_WAP')">支付宝支付</button>
        </template>
        <p v-if="statusMsg" :class="['identity-status', statusType]">{{ statusMsg }}</p>
      </section>
    </template>
  </div>
</template>

<script>
import config from '@/config'
import {
  getOpenIdentityPageInfo,
  submitOpenIdentityVerify,
  submitOpenIdentityPay
} from '@/api/api'

export default {
  name: 'OpenIdentity',
  data () {
    return {
      loading: true,
      loadError: '',
      verifying: false,
      paying: false,
      orderToken: '',
      pageInfo: {},
      form: {
        userName: '',
        identifyNum: '',
        mobile: ''
      },
      statusMsg: '',
      statusType: ''
    }
  },
  computed: {
    identityVerified () {
      return !!this.pageInfo.identityVerified
    },
    presetWayCode () {
      return this.pageInfo.wayCode || ''
    },
    amountText () {
      const amount = this.pageInfo.amount
      if (amount == null) return '--'
      return '¥' + (amount / 100).toFixed(2)
    }
  },
  mounted () {
    document.title = '实人认证'
    this.init()
  },
  methods: {
    init () {
      const token = this.$route.params.token
      this.orderToken = token
      if (!token) {
        this.loading = false
        this.loadError = '链接无效，缺少 token'
        return
      }
      config.cacheToken = token
      getOpenIdentityPageInfo({ token })
        .then(info => {
          this.pageInfo = info || {}
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          this.loadError = (err && err.msg) || '加载订单失败'
        })
    },
    handleVerify () {
      if (!this.form.userName || !this.form.identifyNum || !this.form.mobile) {
        this.showStatus('请完整填写姓名、身份证号和手机号', 'error')
        return
      }
      this.verifying = true
      this.showStatus('核验中，请稍候...', 'success')
      submitOpenIdentityVerify({
        token: this.orderToken,
        userName: this.form.userName,
        identifyNum: this.form.identifyNum,
        mobile: this.form.mobile
      }).then(res => {
        this.verifying = false
        this.pageInfo = Object.assign({}, this.pageInfo, {
          identityVerified: true,
          nameMask: res.nameMask,
          idMask: res.idMask,
          phoneMask: res.phoneMask
        })
        this.statusMsg = ''
      }).catch(err => {
        this.verifying = false
        this.showStatus((err && err.msg) || '核验失败', 'error')
      })
    },
    handlePay (wayCode) {
      const payload = { token: this.orderToken }
      if (wayCode) {
        payload.wayCode = wayCode
      }
      this.paying = true
      submitOpenIdentityPay(payload).then(res => {
        this.paying = false
        this.redirectPay(res.payData)
      }).catch(err => {
        this.paying = false
        this.showStatus((err && err.msg) || '支付失败', 'error')
      })
    },
    redirectPay (payData) {
      if (!payData) {
        this.showStatus('未获取到支付地址', 'error')
        return
      }
      if (payData.indexOf('<form') >= 0 || payData.indexOf('<FORM') >= 0) {
        document.open()
        document.write(payData)
        document.close()
        return
      }
      location.href = payData
    },
    showStatus (msg, type) {
      this.statusMsg = msg
      this.statusType = type
    }
  }
}
</script>

<style scoped>
.identity-page {
  min-height: 100vh;
  background: #ededed;
  padding: 24px;
  box-sizing: border-box;
}
.identity-loading,
.identity-state {
  text-align: center;
  color: #888;
  padding: 120px 24px;
  font-size: 28px;
}
.identity-state.error {
  color: #cf1322;
}
.identity-card {
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}
.identity-title {
  font-size: 36px;
  font-weight: 600;
  margin: 0 0 12px;
  color: #323232;
}
.identity-title-sm {
  font-size: 32px;
  margin-bottom: 20px;
}
.identity-desc {
  font-size: 24px;
  color: #888;
  line-height: 1.6;
  margin: 0 0 16px;
}
.identity-amount {
  font-size: 56px;
  font-weight: 700;
  color: #1677ff;
  margin: 8px 0;
}
.identity-subject,
.identity-mch {
  font-size: 26px;
  color: #666;
  margin: 8px 0 0;
}
.identity-label {
  display: block;
  font-size: 26px;
  color: #666;
  margin: 20px 0 10px;
}
.identity-input {
  width: 100%;
  height: 80px;
  border: 1px solid #e5e6eb;
  border-radius: 12px;
  padding: 0 20px;
  font-size: 28px;
  box-sizing: border-box;
  outline: none;
}
.identity-input:focus {
  border-color: #1677ff;
}
.identity-btn {
  width: 100%;
  height: 88px;
  border: none;
  border-radius: 12px;
  font-size: 30px;
  font-weight: 600;
  margin-top: 24px;
  color: #fff;
}
.identity-btn:disabled {
  opacity: 0.6;
}
.identity-btn-primary {
  background: #1677ff;
}
.identity-btn-wx {
  background: #07c160;
}
.identity-btn-ali {
  background: #1677ff;
}
.identity-status {
  margin-top: 20px;
  padding: 16px;
  border-radius: 10px;
  font-size: 24px;
}
.identity-status.error {
  background: #fff2f0;
  color: #cf1322;
}
.identity-status.success {
  background: #f6ffed;
  color: #389e0d;
}
.identity-verified {
  font-size: 24px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 8px;
}
</style>
